/**
 * PSR Phase 2 — municipal / county open-data catalog discovery (Socrata + data.gov).
 */
import { fetchWithTimeout } from '../dataProviders/types';
import { factToCitationRef } from '../../lib/sentiment/citationRefs';
import { resolveSentimentSiteContext } from './siteContext';
import {
  emptySentimentResult,
  type SentimentFetchOptions,
  type SentimentFetchResult,
  type SentimentFetcher,
} from './types';
import { probeHttpAvailability } from './probeAvailability';
import { rethrowIfAborted, throwIfAborted } from '../../lib/abortSignal';

const MAX_DATASETS = 5;

async function searchSocrataCatalog(
  query: string,
  signal?: AbortSignal,
): Promise<Array<{ name: string; link: string }>> {
  // The federated Socrata Discovery API has NO `data.gov` domain — passing
  // `domains=data.gov&search_context=data.gov` makes it 404 with
  // `{"error":"Domain not found: data.gov"}`. Query the federated catalog
  // across all member Socrata domains (the default) instead. // R-130-10
  const url = `https://api.us.socrata.com/api/catalog/v1?q=${encodeURIComponent(query)}&limit=${MAX_DATASETS}`;
  const res = await fetchWithTimeout(url, 12_000, signal);
  if (!res.ok) return [];
  const json = await res.json() as { results?: Array<{ resource?: { name?: string; link?: string } }> };
  const out: Array<{ name: string; link: string }> = [];
  for (const row of json.results ?? []) {
    const name = row.resource?.name?.trim();
    const link = row.resource?.link?.trim();
    if (name && link) out.push({ name, link });
  }
  return out;
}

export const localPublicDataFetcher: SentimentFetcher = {
  sourceId: 'local-public-data',

  async probeAvailability() {
    // `domains=data.gov` 404s (no such Socrata domain) — probe the bare
    // federated catalog, which mirrors the live `searchSocrataCatalog` URL. // R-130-10
    return probeHttpAvailability({
      url: 'https://api.us.socrata.com/api/catalog/v1?limit=1',
      unconfiguredStatuses: [401, 403],
    }); // R-130-10
  },

  async isAvailable(siteId, opts) {
    const ctx = await resolveSentimentSiteContext(siteId, opts);
    return !!(ctx?.locality || ctx?.county || ctx?.state);
  },

  async fetch(siteId, opts?: SentimentFetchOptions): Promise<SentimentFetchResult> {
    throwIfAborted(opts?.signal);
    try {
      const ctx = await resolveSentimentSiteContext(siteId, opts);
      if (!ctx) {
        return emptySentimentResult('local-public-data', ['local-public-data: no site context']);
      }
      const place = ctx.locality ?? ctx.county ?? ctx.state;
      if (!place) {
        return emptySentimentResult('local-public-data', ['local-public-data: no locality seed']);
      }

      const query = `${place} planning zoning public meeting`;
      const datasets = await searchSocrataCatalog(query, opts?.signal);
      throwIfAborted(opts?.signal);
      if (datasets.length === 0) {
        return emptySentimentResult('local-public-data', ['local-public-data: no open-data datasets matched']);
      }

      const now = new Date().toISOString();
      const facts = datasets.slice(0, MAX_DATASETS).map((d, i) => ({
        key: `open_dataset_${i + 1}`,
        label: 'Local open-data dataset',
        value: d.name.slice(0, 200),
        url: d.link,
        observedAt: now,
      }));

      return {
        sourceId: 'local-public-data',
        coverage: datasets.length >= 2 ? 'full' : 'partial',
        normalizedFacts: facts,
        citationRefs: facts.map((f, i) => factToCitationRef(f, 'local-public-data', i + 1)),
        errors: [],
        fetchedAt: now,
      };
    } catch (e) {
      rethrowIfAborted(opts?.signal, e);
      return emptySentimentResult('local-public-data', [`local-public-data: ${e instanceof Error ? e.message : String(e)}`]);
    }
  },
};
